import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as querySubSourcesApi from './query-sub-sources.api';

import type {
  CreateQuerySubSourceRequest,
  QuerySubSourceListQuery,
  UpdateQuerySubSourceRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: QuerySubSourceListQuery) => ['query-sub-sources', 'list', query] as const,
  detail: (id: string) => ['query-sub-sources', 'detail', id] as const,
  sourceOptions: ['query-sources', 'options'] as const,
};

export const useQuerySubSourcesList = (query: QuerySubSourceListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => querySubSourcesApi.listQuerySubSources(query),
    staleTime: 30_000,
  });

/**
 * Slim query source options for the parent picker. Keyed under
 * `['query-sources', 'options']` so any query-sources feature can share the
 * same cache entry.
 */
export const useQuerySourceOptions = () =>
  useQuery({
    queryKey: QK.sourceOptions,
    queryFn: querySubSourcesApi.listQuerySourceOptions,
    staleTime: 5 * 60_000,
  });

export const useCreateQuerySubSource = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQuerySubSourceRequest) =>
      querySubSourcesApi.createQuerySubSource(payload),
    onSuccess: (qss) => {
      qc.invalidateQueries({ queryKey: ['query-sub-sources', 'list'] });
      toast.success(`Added ${qss.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add query sub source'),
  });
};

export const useUpdateQuerySubSource = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateQuerySubSourceRequest }) =>
      querySubSourcesApi.updateQuerySubSource(id, payload),
    onSuccess: (qss) => {
      qc.invalidateQueries({ queryKey: ['query-sub-sources', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(qss.id) });
      toast.success('Query sub source updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query sub source'),
  });
};

export const useDeleteQuerySubSource = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => querySubSourcesApi.deleteQuerySubSource(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['query-sub-sources', 'list'] });
      toast.success('Query sub source deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete query sub source'),
  });
};

export const useBulkDeleteQuerySubSources = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => querySubSourcesApi.bulkDeleteQuerySubSources(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['query-sub-sources', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete query sub sources'),
  });
};

export const useBulkSetQuerySubSourcesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      querySubSourcesApi.bulkSetQuerySubSourcesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['query-sub-sources', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query sub source status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
